/*
 * The trends report card: a marker picker (aria-pressed chips) above an SVG
 * chart, the latest value, "What this means" and three summary tiles. The
 * card root carries data-trapezium-state="marker-<key>".
 *
 * As in the mockup, two more report pages peek out behind it: one with the
 * LDL, HDL and Triglycerides mini charts, one with a trends summary and a key
 * takeaway. Their values come from the same MARKERS data (decorative copies,
 * hidden from assistive technology; the card in front carries the content).
 */
import { useState } from 'react';
import { Heart, Lightbulb } from 'lucide-react';
import { BrandMark } from '../../mocks/ReportCover';
import { BarsGlyph, CheckDiscGlyph, RiseGlyph, TargetGlyph } from '../../mocks/ProductGlyphs';
import { STATUS_LABELS } from '../../mocks/sampleReport';
import { Status } from './ReportParts';
import TrendChart, { DATES, MARKERS, YEAR_AGO_INDEX, changeOverYear } from './TrendChart';
import './TrendReport.css';

const SCREEN = 'product.trends';

const TILE_GLYPH = { trend: CheckDiscGlyph, watch: RiseGlyph, change: BarsGlyph, range: TargetGlyph };

function Tile({ glyph, tone, label, word, wordTone, note }) {
  const Glyph = TILE_GLYPH[glyph];
  return (
    <li className={`tr-tile tr-tile--${tone}`}>
      <Glyph className="tr-tile__icon" size={22} />
      <span>
        <span className="tr-tile__label">{label}</span>
        <strong className={`tr-tile__word tr-tile__word--${wordTone}`}>{word}</strong>
        <span className="tr-tile__note">{note}</span>
      </span>
    </li>
  );
}

function PatientLines() {
  return (
    <p className="tr-report__patient">
      <strong>Patient B</strong>
      <span>Male | 34 years</span>
      <span>Patient ID: NG-DEMO-0215</span>
      <span>Test Date: 12 Dec 2024</span>
    </p>
  );
}

/* ---------- The pages behind the card (decorative) ---------- */

const LINE = { normal: '#1fa463', borderline: '#d99a00', high: '#e5484d' };
const AREA = { normal: '#e3f5ea', borderline: '#fdf1d2', high: '#fde4e6' };

/* A mini chart of the last year (Jan 24 → Dec 24) for one marker. */
function MiniChart({ marker }) {
  const from = YEAR_AGO_INDEX;
  const values = marker.values.slice(from);
  const lo = Math.min(...values);
  const hi = Math.max(...values);
  const x = (i) => 6 + (108 * i) / (values.length - 1);
  const y = (v) => 40 - ((v - lo) / Math.max(1, hi - lo)) * 28;
  const pts = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const labels = [0, 1, values.length - 2].map((i) => ({ i, d: DATES[from + i] }));
  return (
    <svg className="tr-mini__chart" viewBox="0 0 120 58" aria-hidden="true" focusable="false">
      <rect x="0" y="4" width="120" height="42" rx="3" fill={AREA[marker.status]} />
      <polyline points={pts} fill="none" stroke={LINE[marker.status]} strokeWidth="1.6" />
      {values.map((v, i) => (
        <circle
          key={DATES[from + i]}
          cx={x(i)}
          cy={y(v)}
          r="2.2"
          fill="#fff"
          stroke={LINE[marker.status]}
          strokeWidth="1.4"
        />
      ))}
      {labels.map(({ i, d }) => (
        <text key={d} x={x(i)} y="56" textAnchor="middle" className="tr-mini__date">
          {d}
        </text>
      ))}
    </svg>
  );
}

function MiniMarker({ marker }) {
  const c = changeOverYear(marker);
  const current = marker.values[marker.values.length - 1];
  return (
    <div className="tr-mini">
      <p className="tr-mini__head">
        <span>{marker.name}</span>
        <span className={`tr-pill tr-pill--${marker.status}`}>{STATUS_LABELS[marker.status]}</span>
      </p>
      <div className="tr-mini__body">
        <MiniChart marker={marker} />
        <p className="tr-mini__value">
          <strong>{current}</strong>
          <span>{marker.unit}</span>
          <b className={c.good ? 'is-good' : 'is-watch'}>{`${c.down ? '↓' : '↑'} ${c.percent}%`}</b>
          <span>vs. last year</span>
        </p>
      </div>
    </div>
  );
}

/* Two extra markers for the summary page; synthetic, both in range. */
const SUMMARY_EXTRA = [
  { name: 'Fasting Glucose', status: 'normal' },
  { name: 'HbA1c', status: 'normal' },
];

function BackPages() {
  const minis = MARKERS.filter((m) => m.key !== 'total');
  const summary = [...MARKERS.map((m) => ({ name: m.name, status: m.status })), ...SUMMARY_EXTRA];
  return (
    <div className="tr-back" aria-hidden="true">
      <div className="tr-page tr-page--minis">
        <PatientLines />
        {minis.map((m) => (
          <MiniMarker key={m.key} marker={m} />
        ))}
      </div>
      <div className="tr-page tr-page--summary">
        <PatientLines />
        <p className="tr-summary__title">Trends summary</p>
        <ul className="tr-summary">
          {summary.map((row) => (
            <li key={row.name}>
              <span>{row.name}</span>
              <span className={`tr-pill tr-pill--${row.status}`}>{STATUS_LABELS[row.status]}</span>
            </li>
          ))}
        </ul>
        <div className="tr-takeaway">
          <p className="tr-takeaway__title">
            <Lightbulb size={18} strokeWidth={2} aria-hidden="true" />
            Key takeaway
          </p>
          <p>
            Most markers are within normal range and show improving trends. Continue regular checkups to maintain
            progress.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function TrendReport() {
  const [key, setKey] = useState('total');
  const m = MARKERS.find((x) => x.key === key);
  const current = m.values[m.values.length - 1];
  const change = changeOverYear(m);

  return (
    <div className="tr-stack">
      <BackPages />
      <article className="card tr-report" data-trapezium-state={`marker-${m.key}`} aria-labelledby="tr-report-title">
        <header className="tr-report__head">
          <div>
            <span className="tr-brand">
              <BrandMark className="tr-brand__mark" />
              niroggyan
            </span>
            <h2 id="tr-report-title" className="tr-report__title">
              Smart Health Report
            </h2>
            <p className="tr-report__tagline">Trends and insights for a healthier you</p>
          </div>
          <PatientLines />
        </header>

        <div className="tr-picker" role="group" aria-label="Marker">
          <span className="tr-picker__label" aria-hidden="true">
            Marker
          </span>
          {MARKERS.map((x, i) => (
            <button
              key={x.key}
              type="button"
              className={`tr-chip hint${x.key === key ? ' is-active hint--active' : ''}`}
              style={{ '--hint-i': i }}
              aria-pressed={x.key === key}
              onClick={() => setKey(x.key)}
              data-trapezium-id={`${SCREEN}.marker-${x.key}`}
            >
              {x.button}
            </button>
          ))}
        </div>

        <div className="tr-marker">
          <div className="tr-marker__head">
            <Heart className="tr-marker__heart" size={24} fill="currentColor" strokeWidth={0} aria-hidden="true" />
            <h3 className="tr-marker__name">{m.name}</h3>
            <Status status={m.status} />
          </div>

          <div className="tr-marker__body">
            <div className="tr-marker__chart">
              <p className="tr-marker__axis">{`${m.name} (${m.unit})`}</p>
              <TrendChart marker={m} />
            </div>
            <p className="tr-latest">
              <strong>{current}</strong>
              <span className="tr-latest__unit">{m.unit}</span>
              <span className={`tr-latest__change tr-latest__change--${change.good ? 'good' : 'watch'}`}>
                {`${change.down ? '↓' : '↑'} ${change.percent}%`}
              </span>
              <span className="tr-latest__note">vs. last year</span>
            </p>
          </div>

          <div className="tr-meaning">
            <Lightbulb size={24} strokeWidth={2} aria-hidden="true" />
            <div>
              <h4 className="tr-meaning__title">What this means</h4>
              <p>{m.meaning}</p>
            </div>
          </div>

          <ul className="tr-tiles" aria-label={`${m.name} at a glance`}>
            <Tile
              glyph={m.trend.good ? 'trend' : 'watch'}
              tone={m.trend.good ? 'green' : 'amber'}
              label="Trend"
              word={m.trend.word}
              wordTone={m.trend.good ? 'good' : 'watch'}
              note={m.trend.note}
            />
            <Tile
              glyph="change"
              tone="blue"
              label="Change"
              word={`${change.down ? '▼' : '▲'} ${change.amount} ${m.unit}`}
              wordTone={change.good ? 'good' : 'watch'}
              note={`From ${change.from} to ${change.to}`}
            />
            <Tile
              glyph="range"
              tone="purple"
              label="Range"
              word={m.range.word}
              wordTone={m.range.good ? 'good' : 'watch'}
              note={m.range.note}
            />
          </ul>
        </div>
      </article>
    </div>
  );
}
